import React, { useState, useRef, useEffect } from "react";
import QRCode from "qrcode";
import { buttonClass, IconButton } from "./ui/Button";
import { Alert } from "./ui/Alert";
import { Dialog } from "./ui/Dialog";
import EmptyState from "./ui/EmptyState";
import FilePick from "./ui/FilePick";
import Tooltip from "./ui/Tooltip";
import { IconClose } from "./ui/icons";
import systemsLogo from "../img/systems-logo.svg";

export interface IDCardUser {
  id: number | string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

export interface IDCard {
  id: number | string;
  fullName: string;
  email: string;
  photo: string | null;
  qrCode: string;
}

interface IDCardGeneratorProps {
  user: IDCardUser;
  onClose: () => void;
  onAddToCart?: (card: IDCard) => void;
  cartCount?: number;
}

const IDCardGenerator = ({ user, onClose, onAddToCart, cartCount = 0 }: IDCardGeneratorProps) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [addedToCart, setAddedToCart] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const fullName = `${user.firstname || ""}${user.lastname ? ` ${user.lastname}` : ""}`.trim();
  const email = user.email || "";

  // Handle paste from clipboard
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
              setImageError("Image must be less than 5MB");
              return;
            }
            setImageError("");
            const reader = new FileReader();
            reader.onload = (event) => {
              setUploadedImage(event.target?.result as string);
            };
            reader.onerror = () => {
              setImageError("Failed to read pasted image");
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  // Generate QR code when email is available
  useEffect(() => {
    if (email) {
      QRCode.toDataURL(email, {
        width: 200,
        margin: 2,
        errorCorrectionLevel: "H", // Highest error correction
      })
        .then((url) => setQrCodeDataUrl(url))
        .catch((err) => console.error("QR code generation error:", err));
    }
  }, [email]);

  const handleImageUpload = (files: File[]) => {
    const file = files[0];
    setImageError("");

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setImageError("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target?.result as string);
    };
    reader.onerror = () => {
      setImageError("Failed to read image file");
    };
    reader.readAsDataURL(file);
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart({
        id: user.id,
        fullName,
        email,
        photo: uploadedImage,
        qrCode: qrCodeDataUrl,
      });
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 1500);
    }
  };

  const handlePrint = () => {
    const printContent = cardRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print the ID card");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ID Card - ${fullName}</title>
          <style>
            @page {
              size: 2.125in 3.375in;
              margin: 0;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #f0f0f0;
            }
            .card {
              width: 2.125in;
              height: 3.375in;
              background: white;
              border: 2px solid #000;
              border-radius: 8px;
              overflow: hidden;
              display: flex;
              flex-direction: column;
            }
            .clip-space {
              height: 0.5in;
            }
            .header {
              background: white;
              padding: 8px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .logo {
              height: 32px;
              width: auto;
            }
            .photo-section {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 6px;
              background: #f8f9fa;
            }
            .photo {
              width: 80px;
              height: 80px;
              border: 2px solid #ddd;
              border-radius: 4px;
              object-fit: cover;
              background: #e9ecef;
            }
            .photo-placeholder {
              width: 80px;
              height: 80px;
              border: 2px dashed #ccc;
              border-radius: 4px;
              display: flex;
              justify-content: center;
              align-items: center;
              color: #999;
              font-size: 10px;
              text-align: center;
              padding: 8px;
              background: #f5f5f5;
            }
            .info-section {
              padding: 4px 8px;
              text-align: center;
              border-top: 1px solid #eee;
            }
            .name {
              font-size: 14px;
              font-weight: bold;
              color: #1a1a2e;
              word-break: break-word;
            }
            .qr-section {
              padding: 4px 8px 6px;
              background: white;
              display: flex;
              justify-content: center;
            }
            .qr-code {
              width: 70px;
              height: 70px;
            }
            @media print {
              body {
                background: white;
              }
              .card {
                border: 2px solid #000;
              }
            }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="clip-space"></div>
            <div class="header">
              <img src="${systemsLogo}" alt="Logo" class="logo" />
            </div>
            <div class="photo-section">
              ${uploadedImage
                ? `<img src="${uploadedImage}" alt="Photo" class="photo" />`
                : `<div class="photo-placeholder">No Photo</div>`
              }
            </div>
            <div class="info-section">
              <div class="name">${fullName || "Unknown"}</div>
            </div>
            <div class="qr-section">
              ${qrCodeDataUrl ? `<img src="${qrCodeDataUrl}" alt="QR Code" class="qr-code" />` : ""}
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Generate ID Card"
      size="md"
      footer={
        <>
          <button
            onClick={onClose}
            className={buttonClass('quiet', 'md')}
          >
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            disabled={addedToCart}
            className={buttonClass(addedToCart ? 'good' : 'info', 'md')}
          >
            {addedToCart ? "Added!" : `Add to Cart${cartCount > 0 ? ` (${cartCount})` : ""}`}
          </button>
          <button
            onClick={handlePrint}
            className={buttonClass('filled', 'md')}
          >
            Print
          </button>
        </>
      }
    >
        <div className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm text-on-surface-variant mb-2">
              Upload Photo (optional)
            </label>
            <FilePick
              accept="image/*"
              onFiles={handleImageUpload}
              variant="quiet"
              className="w-full"
            >
              {uploadedImage ? "Change Photo" : "Select Photo"}
            </FilePick>
            <p className="text-xs text-on-surface-variant mt-1 text-center">
              or paste an image from clipboard (Ctrl+V / Cmd+V)
            </p>
            {imageError && (
              <Alert tone="error" variant="text" className="mt-1">{imageError}</Alert>
            )}
          </div>

          {/* Card Preview */}
          <div className="border border-outline-variant rounded-lg p-4 bg-gray-900">
            <p className="text-sm text-on-surface-variant mb-2 text-center">Preview</p>
            <div
              ref={cardRef}
              className="mx-auto bg-white rounded-lg overflow-hidden"
              style={{ width: "212.5px", height: "337.5px", border: "2px solid #000" }}
            >
              {/* Clip space */}
              <div style={{ height: "38px" }} />

              {/* Header with logo */}
              <div
                className="flex justify-center items-center p-2 bg-white"
              >
                <img
                  src={systemsLogo}
                  alt="Logo"
                  style={{ height: "32px", width: "auto" }}
                />
              </div>

              {/* Photo section */}
              <div
                className="flex justify-center items-center"
                style={{ background: "#f8f9fa", padding: "6px" }}
              >
                {uploadedImage ? (
                  <img
                    src={uploadedImage}
                    alt="Photo"
                    className="object-cover border-2 border-gray-300 rounded"
                    style={{ width: "80px", height: "80px" }}
                  />
                ) : (
                  <div
                    className="border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs text-center p-2"
                    style={{
                      width: "80px",
                      height: "80px",
                      background: "#f5f5f5",
                    }}
                  >
                    No Photo
                  </div>
                )}
              </div>

              {/* Info section */}
              <div
                className="text-center"
                style={{ borderTop: "1px solid #eee", padding: "4px 8px" }}
              >
                <div
                  className="font-bold text-gray-900"
                  style={{ fontSize: "14px" }}
                >
                  {fullName || "Unknown"}
                </div>
              </div>

              {/* QR Code section */}
              <div className="flex justify-center bg-white" style={{ padding: "4px 8px 6px" }}>
                {qrCodeDataUrl && (
                  <img src={qrCodeDataUrl} alt="QR Code" style={{ width: "70px", height: "70px" }} />
                )}
              </div>
            </div>
          </div>
        </div>
    </Dialog>
  );
};

// Print Cart Component
interface IDCardPrintCartProps {
  cards: IDCard[];
  onRemove: (index: number) => void;
  onClear: () => void;
  onClose: () => void;
}

export const IDCardPrintCart = ({ cards, onRemove, onClear, onClose }: IDCardPrintCartProps) => {
  const handlePrintAll = () => {
    if (cards.length === 0) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print the ID cards");
      return;
    }

    const cardsHtml = cards.map((card) => `
      <div class="card">
        <div class="clip-space"></div>
        <div class="header">
          <img src="${systemsLogo}" alt="Logo" class="logo" />
        </div>
        <div class="photo-section">
          ${card.photo
            ? `<img src="${card.photo}" alt="Photo" class="photo" />`
            : `<div class="photo-placeholder">No Photo</div>`
          }
        </div>
        <div class="info-section">
          <div class="name">${card.fullName || "Unknown"}</div>
        </div>
        <div class="qr-section">
          ${card.qrCode ? `<img src="${card.qrCode}" alt="QR Code" class="qr-code" />` : ""}
        </div>
      </div>
    `).join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ID Cards (${cards.length})</title>
          <style>
            @page {
              size: letter;
              margin: 0.25in;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Arial, sans-serif;
              background: #f0f0f0;
            }
            .cards-container {
              display: flex;
              flex-wrap: wrap;
              gap: 0.125in;
              justify-content: flex-start;
              padding: 0.125in;
            }
            .card {
              width: 2.125in;
              height: 3.375in;
              background: white;
              border: 2px solid #000;
              border-radius: 8px;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              page-break-inside: avoid;
            }
            .clip-space {
              height: 0.5in;
            }
            .header {
              background: white;
              padding: 8px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .logo {
              height: 32px;
              width: auto;
            }
            .photo-section {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 6px;
              background: #f8f9fa;
            }
            .photo {
              width: 80px;
              height: 80px;
              border: 2px solid #ddd;
              border-radius: 4px;
              object-fit: cover;
              background: #e9ecef;
            }
            .photo-placeholder {
              width: 80px;
              height: 80px;
              border: 2px dashed #ccc;
              border-radius: 4px;
              display: flex;
              justify-content: center;
              align-items: center;
              color: #999;
              font-size: 10px;
              text-align: center;
              padding: 8px;
              background: #f5f5f5;
            }
            .info-section {
              padding: 4px 8px;
              text-align: center;
              border-top: 1px solid #eee;
            }
            .name {
              font-size: 14px;
              font-weight: bold;
              color: #1a1a2e;
              word-break: break-word;
            }
            .qr-section {
              padding: 4px 8px 6px;
              background: white;
              display: flex;
              justify-content: center;
            }
            .qr-code {
              width: 70px;
              height: 70px;
            }
            @media print {
              body {
                background: white;
              }
              .card {
                border: 2px solid #000;
              }
            }
          </style>
        </head>
        <body>
          <div class="cards-container">
            ${cardsHtml}
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Print Cart (${cards.length} card${cards.length !== 1 ? "s" : ""})`}
      size="xl"
      footer={
        cards.length > 0 ? (
          <>
            <button
              onClick={onClear}
              className={buttonClass('danger', 'md')}
            >
              Clear All
            </button>
            <button
              onClick={handlePrintAll}
              className={buttonClass('filled', 'md')}
            >
              Print All ({cards.length})
            </button>
          </>
        ) : undefined
      }
    >
        <div>
          {cards.length === 0 ? (
            <EmptyState variant="area">
              No cards in cart. Add cards from the user list.
            </EmptyState>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {cards.map((card, index) => (
                  <div
                    key={`${card.id}-${index}`}
                    className="bg-gray-700 rounded-lg p-2 relative"
                  >
                    <Tooltip label="Remove card">
                    <IconButton
                      label="Remove card"
                      size="sm"
                      onClick={() => onRemove(index)}
                      className="absolute -top-2 -right-2 !bg-surface-container-highest !text-red-300 hover:!text-red-200"
                    >
                      <IconClose />
                    </IconButton>
                    </Tooltip>
                    <div className="bg-white rounded p-1 mb-2">
                      {card.photo ? (
                        <img
                          src={card.photo}
                          alt={card.fullName}
                          className="w-full h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-full h-16 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                          No Photo
                        </div>
                      )}
                    </div>
                    <p className="text-white text-sm text-center truncate">
                      {card.fullName}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
    </Dialog>
  );
};

export default IDCardGenerator;
